import { cn } from '@/lib/utils';
import DOMPurify from 'dompurify';
import MarkdownIt from 'markdown-it';
import { useMemo } from 'react';

// markdown-it with `html: false` escapes raw HTML from the source so the only
// markup we emit comes from markdown syntax (links, lists, code, etc.).
// DOMPurify on top guards against any future option drift or a markdown-it bug —
// defence in depth for content that ultimately renders as HTML.
const md = new MarkdownIt({ html: false, linkify: true, breaks: true });

interface MarkdownContentProps {
  /** Markdown source to render. */
  content?: string | null;
  /** Extra classes merged onto the prose wrapper. */
  className?: string;
}

/**
 * Renders trusted-but-defensively-sanitized markdown as styled HTML using the
 * `@tailwindcss/typography` `prose` classes. Returns `null` for empty content so
 * callers can render their own empty state.
 */
export function MarkdownContent({ content, className }: MarkdownContentProps) {
  const html = useMemo(() => {
    if (!content?.trim()) return '';
    return DOMPurify.sanitize(md.render(content), {
      USE_PROFILES: { html: true },
    });
  }, [content]);

  if (!html) return null;

  return (
    <div
      className={cn('prose prose-sm max-w-none dark:prose-invert break-words', className)}
      // Sanitized above with DOMPurify; markdown-it runs with html:false.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
